import { Eye, Pencil, Users } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export type ShareParticipantRole = "author" | "viewer" | "editor";

export interface ShareParticipant {
  userId: string;
  name: string;
  initials: string;
  avatarUrl?: string | null;
  role: ShareParticipantRole;
}

const MAX_AVATARS = 3;

function roleLabel(role: ShareParticipantRole): string {
  if (role === "author") return "auteur";
  return role === "editor" ? "édition" : "lecture seule";
}

function roleIcon(role: ShareParticipantRole): JSX.Element | null {
  if (role === "author") return null;
  const Icon = role === "editor" ? Pencil : Eye;
  return <Icon className="h-3 w-3 text-grey-500" aria-hidden />;
}

export function ShareParticipantsGlyph({
  participants,
  visibility,
  size = "sm",
  className,
}: {
  participants: ShareParticipant[];
  visibility?: "private" | "cabinet";
  size?: "xs" | "sm";
  className?: string;
}): JSX.Element | null {
  // The glyph is used in dense rows that are not always under a page-level
  // TooltipProvider (notably the mobile case list), so it owns its provider.
  if (visibility === "cabinet") {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={cn(
                "inline-flex flex-shrink-0 items-center justify-center rounded-full bg-grey-100",
                size === "xs" ? "h-4 w-4" : "h-6 w-6",
                className,
              )}
              aria-label="Partagé à tout le cabinet"
            >
              <Users className={cn("text-grey-500", size === "xs" ? "h-2.5 w-2.5" : "h-3.5 w-3.5")} aria-hidden />
            </span>
          </TooltipTrigger>
          <TooltipContent>Visible par tout le cabinet</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (participants.length === 0) return null;

  const shown = participants.slice(0, MAX_AVATARS);
  const extra = participants.length - shown.length;
  const avatarClass = size === "xs" ? "h-4 w-4" : "h-6 w-6";
  const fallbackClass = size === "xs" ? "text-[6px]" : "text-[10px]";

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn("relative inline-flex flex-shrink-0 items-center", className)}>
            <div className={cn("flex items-center", size === "xs" ? "-space-x-1" : "-space-x-1.5")}>
              {shown.map((p) => (
                <Avatar key={`${p.role}-${p.userId}`} className={cn(avatarClass, "ring-white", size === "xs" ? "ring-[1.5px]" : "ring-2")}>
                  <AvatarImage src={p.avatarUrl || undefined} />
                  <AvatarFallback
                    className={cn(
                      fallbackClass,
                      "font-semibold",
                      p.role === "author" ? "bg-charcoal text-white" : "bg-grey-100 text-grey-700",
                    )}
                  >
                    {p.initials}
                  </AvatarFallback>
                </Avatar>
              ))}
            </div>
            {extra > 0 && (
              <span
                className={cn(
                  "absolute -bottom-1 -right-1 inline-flex items-center justify-center rounded-full bg-charcoal font-semibold leading-none text-white ring-white",
                  size === "xs" ? "h-3 min-w-[0.75rem] px-0.5 text-[7px] ring-[1.5px]" : "h-4 min-w-[1rem] px-1 text-[9px] ring-2",
                )}
              >
                +{extra}
              </span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-[260px] p-1.5">
          <div className="space-y-1">
            {participants.map((p) => (
              <div key={`${p.role}-${p.userId}`} className="flex min-w-0 items-center gap-2 text-xs">
                {roleIcon(p.role)}
                <span className="min-w-0 flex-1 truncate text-charcoal">{p.name}</span>
                <span className="flex-shrink-0 text-grey-500">{roleLabel(p.role)}</span>
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
